import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import FitToWidth from '../../components/Games2/FitToWidth';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

// ============== Constants ==============
const TOTAL_ROUNDS = 20;
const CLOCKS_PER_GRID = 9;
const CRITICAL_COUNT = 4; // 4 clocks match the critical attribute
const HOURS = 8; // 8-hour clock

const SPEED_CONFIG = {
  slow: { displayTime: 5000, attentionTime: 2500, attributeTime: 2500 },
  medium: { displayTime: 3500, attentionTime: 2500, attributeTime: 2500 },
  fast: { displayTime: 2800, attentionTime: 2500, attributeTime: 2500 },
};

const LEVEL_CONFIG = {
  1: { speed: 'slow', feedback: true },
  2: { speed: 'medium', feedback: false },
  3: { speed: 'fast', feedback: false },
};

// Possible critical rules — single property only
const CRITICAL_RULES = [
  { type: 'color', value: 'black', label: 'Black instruments' },
  { type: 'color', value: 'white', label: 'White instruments' },
  { type: 'shape', value: 'round', label: 'Round instruments' },
  { type: 'shape', value: 'square', label: 'Square instruments' },
];

const ORDINALS = ['first', 'second', 'third', 'fourth'];

// ============== Clock Generation ==============

// Get 3 random unique positions (out of 8) for visible numbers
const getVisiblePositions = () => {
  const positions = [1, 2, 3, 4, 5, 6, 7, 8];
  const shuffled = positions.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
};

// Generate a random pointer value pointing directly at an hour position
// Returns exact values like 1, 2, 3, 4, 5, 6, 7, or 8
const getRandomPointerValue = () => {
  return Math.floor(Math.random() * HOURS) + 1; // 1-8
};

// Generate a single clock instrument
const generateClock = (color, shape) => {
  // Random rotation offset (1-7, never 0) determines where "1" starts on the clock
  const rotationOffset = Math.floor(Math.random() * (HOURS - 1)) + 1; // 1-7
  
  // Get visible number positions first
  const visibleNumbers = getVisiblePositions();
  
  // Generate pointer value that doesn't point at any visible number
  let pointerValue;
  const availablePositions = [1, 2, 3, 4, 5, 6, 7, 8].filter(pos => !visibleNumbers.includes(pos));
  pointerValue = availablePositions[Math.floor(Math.random() * availablePositions.length)];
  
  return {
    color,       // 'black' or 'white'
    shape,       // 'round' or 'square'
    pointerValue,
    visibleNumbers,
    rotationOffset, // Where the sequence starts (1-7, never 0)
  };
};

// Check if a clock matches the critical rule
const matchesRule = (clock, rule) => {
  if (rule.type === 'color') return clock.color === rule.value;
  if (rule.type === 'shape') return clock.shape === rule.value;
  return false;
};

// Pick a random color/shape
const randomColor = () => Math.random() < 0.5 ? 'black' : 'white';
const randomShape = () => Math.random() < 0.5 ? 'round' : 'square';

// Generate a round: 9 clocks in 3x3 grid, exactly 4 matching the critical rule
const generateRound = () => {
  const rule = CRITICAL_RULES[Math.floor(Math.random() * CRITICAL_RULES.length)];
  
  // Generate 4 critical clocks that match the rule
  const criticalClocks = [];
  for (let i = 0; i < CRITICAL_COUNT; i++) {
    let color, shape;
    if (rule.type === 'color') {
      color = rule.value;
      shape = randomShape(); // shape can be anything
    } else {
      shape = rule.value;
      color = randomColor(); // color can be anything
    }
    criticalClocks.push({
      ...generateClock(color, shape),
      isCritical: true,
    });
  }
  
  // Generate 5 non-critical clocks that do NOT match the rule
  const nonCriticalClocks = [];
  for (let i = 0; i < CLOCKS_PER_GRID - CRITICAL_COUNT; i++) {
    let color, shape;
    if (rule.type === 'color') {
      // Opposite color, any shape
      color = rule.value === 'black' ? 'white' : 'black';
      shape = randomShape();
    } else {
      // Opposite shape, any color
      shape = rule.value === 'round' ? 'square' : 'round';
      color = randomColor();
    }
    nonCriticalClocks.push({
      ...generateClock(color, shape),
      isCritical: false,
    });
  }
  
  // Combine and shuffle into 9 positions
  const allClocks = [...criticalClocks, ...nonCriticalClocks];
  const shuffled = allClocks.sort(() => Math.random() - 0.5);
  
  // Track critical positions (left-to-right, top-to-bottom order)
  const criticalPositions = [];
  shuffled.forEach((clock, idx) => {
    if (clock.isCritical) {
      criticalPositions.push(idx);
    }
  });
  criticalPositions.sort((a, b) => a - b);
  
  // The correct answers are the pointer values of critical clocks in order
  const correctAnswers = criticalPositions.map(pos => shuffled[pos].pointerValue);
  
  return {
    criticalAttribute: rule,
    clocks: shuffled,
    criticalPositions,
    correctAnswers,
  };
};

// ============== Clock SVG Component ==============
const ClockInstrument = ({ clock, size = 140, highlight = false }) => {
  const { color, shape, pointerValue, visibleNumbers, rotationOffset = 0 } = clock;
  const isBlack = color === 'black';
  const isRound = shape === 'round';
  
  const bgColor = isBlack ? '#000000' : '#FFFFFF';
  const fgColor = isBlack ? '#FFFFFF' : '#000000';
  const borderColor = '#000000';
  
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.44;
  const borderWidth = size * 0.02;
  const tickOuterRadius = radius * 0.92;
  const tickLength = size * 0.055;
  const tickWidth = size * 0.018;
  const numberRadius = radius * 0.78;
  const pointerLength = radius * 0.55;
  const pointerWidth = size * 0.04;
  
  // Map logical number (1-8) to physical position based on rotation offset
  // If rotationOffset is 0, number 1 is at top (position 0)
  // If rotationOffset is 2, number 1 is at position 2, number 2 is at position 3, etc.
  const getPhysicalPosition = (logicalNumber) => {
    return ((logicalNumber - 1 + rotationOffset) % HOURS);
  };
  
  // Calculate angle for a physical position (0-7, starting from top, clockwise)
  const getAngle = (position) => (position / HOURS) * 360 - 90; // -90 to start from top
  const getAngleRad = (position) => (getAngle(position) * Math.PI) / 180;
  
  // Pointer angle - convert logical pointer value to physical position
  const pointerPhysicalPosition = getPhysicalPosition(pointerValue);
  const pointerAngle = getAngleRad(pointerPhysicalPosition);
  const pointerX = cx + Math.cos(pointerAngle) * pointerLength;
  const pointerY = cy + Math.sin(pointerAngle) * pointerLength;
  
  // Set of logical numbers that should be visible (convert to physical positions for rendering)
  const visiblePhysicalPositions = new Set(visibleNumbers.map(num => getPhysicalPosition(num)));
  
  // Highlight border for critical clocks during feedback
  const highlightStyle = highlight ? { boxShadow: '0 0 0 3px #22C55E' } : {};
  
  return (
    <div 
      className="inline-block"
      style={{ width: size, height: size, ...highlightStyle }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background */}
        {isRound ? (
          <circle cx={cx} cy={cy} r={radius} fill={bgColor} stroke={borderColor} strokeWidth={borderWidth} />
        ) : (
          <rect 
            x={cx - radius} y={cy - radius} 
            width={radius * 2} height={radius * 2} 
            fill={bgColor} stroke={borderColor} strokeWidth={borderWidth} 
          />
        )}
        
        {/* Tick marks — only where there is NO number */}
        {Array.from({ length: HOURS }, (_, i) => i).map(position => {
          if (visiblePhysicalPositions.has(position)) return null;
          const angle = getAngleRad(position);
          const outerX = cx + Math.cos(angle) * tickOuterRadius;
          const outerY = cy + Math.sin(angle) * tickOuterRadius;
          const innerX = cx + Math.cos(angle) * (tickOuterRadius - tickLength);
          const innerY = cy + Math.sin(angle) * (tickOuterRadius - tickLength);
          
          return (
            <line
              key={`tick-${position}`}
              x1={innerX} y1={innerY}
              x2={outerX} y2={outerY}
              stroke={fgColor}
              strokeWidth={tickWidth}
              strokeLinecap="butt"
            />
          );
        })}
        
        {/* Visible numbers — placed at their rotated physical positions */}
        {visibleNumbers.map(logicalNumber => {
          const physicalPosition = getPhysicalPosition(logicalNumber);
          const angle = getAngleRad(physicalPosition);
          const numX = cx + Math.cos(angle) * numberRadius;
          const numY = cy + Math.sin(angle) * numberRadius;
          
          return (
            <text
              key={`num-${logicalNumber}`}
              x={numX}
              y={numY}
              textAnchor="middle"
              dominantBaseline="central"
              fill={fgColor}
              fontSize={size * 0.16}
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              {logicalNumber}
            </text>
          );
        })}
        
        {/* Pointer */}
        <line
          x1={cx} y1={cy}
          x2={pointerX} y2={pointerY}
          stroke={fgColor}
          strokeWidth={pointerWidth}
          strokeLinecap="round"
        />
        
        {/* Center dot */}
        <circle cx={cx} cy={cy} r={size * 0.03} fill={fgColor} />
      </svg>
    </div>
  );
};

// ============== Numpad Component ==============
const Numpad = ({ onSelect, disabled = false, selectedValue = null }) => {
  const buttons = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 'no idea'],
  ];
  
  return (
    <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
      {buttons.flat().map((val) => {
        const isNoIdea = val === 'no idea';
        const isSelected = selectedValue === val || (isNoIdea && selectedValue === 0);
        
        return (
          <button
            key={val}
            onClick={() => onSelect(isNoIdea ? 0 : val)}
            disabled={disabled}
            className={`
              py-3 px-4 rounded-lg font-bold text-lg transition-all
              ${isSelected 
                ? 'bg-cyan-600 text-white ring-2 ring-cyan-400 scale-105' 
                : 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-md hover:shadow-lg'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-95'}
            `}
          >
            {isNoIdea ? 'no idea' : val}
          </button>
        );
      })}
    </div>
  );
};

// ============== Main Game Component ==============
const OWTGame = () => {
  // Debug overlays reveal answers; only staff may toggle them (Shift+O)
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  const settings = location.state || {};
  
  const level = settings.level || 1;
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];
  const speedConfig = SPEED_CONFIG[config.speed];
  const feedbackEnabled = settings.feedback !== undefined ? settings.feedback : config.feedback;
  
  // Game state
  const [gamePhase, setGamePhase] = useState('intro');
  // Phases: intro, attribute, display, input, feedback, results
  const [currentRound, setCurrentRound] = useState(0);
  const [roundData, setRoundData] = useState(null);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0); // 0-3 for 4 critical instruments
  const [userAnswers, setUserAnswers] = useState([]); // answers for current round
  const [roundsData, setRoundsData] = useState([]); // all rounds results
  const [introCountdown, setIntroCountdown] = useState(3);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  
  // Debug
  const [showDebug, setShowDebug] = useState(false);
  
  // Refs
  const gameStartedRef = useRef(null);
  const abortRef = useRef(false);
  const timerRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    abortRef.current = false;
    gameStartedRef.current = Date.now();
    return () => {
      abortRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  
  // Debug toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isStaff && e.shiftKey && e.key === 'O') {
        setShowDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStaff]);
  
  // Intro countdown
  useEffect(() => {
    if (gamePhase !== 'intro') return;
    if (introCountdown <= 0) {
      startRound();
      return;
    }
    const timer = setTimeout(() => setIntroCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [gamePhase, introCountdown]);
  
  // Start a new round
  const startRound = useCallback(() => {
    const data = generateRound();
    setRoundData(data);
    setUserAnswers([]);
    setCurrentQuestionIdx(0);
    setGamePhase('attention');
    
    // Show "Attention, Next item" for 2.5s, then show attribute text for 2.5s, then grid
    timerRef.current = setTimeout(() => {
      if (abortRef.current) return;
      setGamePhase('attribute');
      
      timerRef.current = setTimeout(() => {
        if (abortRef.current) return;
        setGamePhase('display');
        
        // Show grid for displayTime, then switch to input
        timerRef.current = setTimeout(() => {
          if (abortRef.current) return;
          setGamePhase('input');
        }, speedConfig.displayTime);
      }, speedConfig.attributeTime);
    }, speedConfig.attentionTime);
  }, [speedConfig]);
  
  // Handle numpad selection
  const handleAnswer = useCallback((value) => {
    if (gamePhase !== 'input') return;
    
    const newAnswers = [...userAnswers, value];
    setUserAnswers(newAnswers);
    
    if (newAnswers.length < CRITICAL_COUNT) {
      // Move to next question
      setCurrentQuestionIdx(prev => prev + 1);
    } else {
      // All 4 questions answered - evaluate round
      const correctAnswers = roundData.correctAnswers;
      const correctCount = newAnswers.reduce((count, ans, idx) => {
        return count + (ans === correctAnswers[idx] ? 1 : 0);
      }, 0);
      
      const roundResult = {
        round: currentRound + 1,
        criticalAttribute: roundData.criticalAttribute,
        criticalPositions: roundData.criticalPositions,
        correctAnswers: roundData.correctAnswers,
        userAnswers: newAnswers,
        correctCount,
        totalQuestions: CRITICAL_COUNT,
      };
      
      const newRoundsData = [...roundsData, roundResult];
      setRoundsData(newRoundsData);
      
      if (feedbackEnabled) {
        setGamePhase('feedback');
      } else {
        // Move to next round or results
        setTimeout(() => {
          if (currentRound + 1 >= TOTAL_ROUNDS) {
            finishGame(newRoundsData);
          } else {
            setCurrentRound(prev => prev + 1);
          }
        }, 300);
      }
    }
  }, [gamePhase, userAnswers, roundData, currentRound, roundsData, feedbackEnabled]);
  
  // Keyboard support for numpad (1-8 keys)
  useEffect(() => {
    if (gamePhase !== 'input') return;
    const handleKeyDown = (e) => {
      const key = parseInt(e.key);
      if (key >= 1 && key <= 8) {
        handleAnswer(key);
      } else if (e.key === '0' || e.key === '9') {
        handleAnswer(0); // "no idea"
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gamePhase, handleAnswer]);
  
  // Auto-advance round when currentRound changes
  useEffect(() => {
    if (currentRound > 0 && gamePhase !== 'results') {
      startRound();
    }
  }, [currentRound]);
  
  // Continue after feedback
  const handleContinue = useCallback(() => {
    if (currentRound + 1 >= TOTAL_ROUNDS) {
      finishGame(roundsData);
    } else {
      setCurrentRound(prev => prev + 1);
    }
  }, [currentRound, roundsData]);
  
  // Finish game
  const finishGame = useCallback(async (allRoundsData) => {
    setGamePhase('results');
    
    const totalCorrect = allRoundsData.reduce((sum, r) => sum + r.correctCount, 0);
    const totalQuestions = allRoundsData.length * CRITICAL_COUNT;
    const rawPercentage = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;
    
    try {
      await apiClient.post('/api/courses/owt-game/runs/', {
        subject: subjectId,
        level: level,
        feedback_enabled: feedbackEnabled,
        score_multiplier: 1.0,
        rounds_data: allRoundsData,
        started_at: new Date(gameStartedRef.current).toISOString(),
      });
    } catch (error) {
      console.error('Failed to save OWT game results:', error);
    }
  }, [subjectId, level, feedbackEnabled]);
  
  // Exit handlers
  const handleExit = () => setShowExitConfirm(true);
  const confirmExit = () => {
    abortRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    navigate(`${gamesBase}/owt/${subjectId}`);
  };
  
  // Calculate results
  const totalCorrect = roundsData.reduce((sum, r) => sum + r.correctCount, 0);
  const totalQuestions = roundsData.length * CRITICAL_COUNT;
  const finalPercentage = totalQuestions > 0 ? ((totalCorrect / totalQuestions) * 100).toFixed(1) : '0.0';
  
  // Format attribute for display
  const formatAttribute = (attr) => {
    if (!attr) return '';
    return attr.label || `${attr.value} instruments`;
  };

  return (
    <div className="fixed inset-0 top-16 bg-white flex flex-col overflow-hidden" style={{ animation: 'fadeFromBlack 1.5s ease-out' }}>
      <style>{`
        @keyframes fadeFromBlack {
          0% { filter: brightness(0); }
          100% { filter: brightness(1); }
        }
      `}</style>
      
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-900">OWT</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            level === 1 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
            level === 2 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
            'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
          }`}>
            {level === 3 ? 'Real Test' : level === 2 ? 'Training' : 'Practice'}
          </span>
          {gamePhase !== 'intro' && gamePhase !== 'results' && (
            <span className="text-xs text-gray-500">
              Round {Math.min(currentRound + 1, TOTAL_ROUNDS)}/{TOTAL_ROUNDS}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Score: {totalCorrect}/{totalQuestions}
          </span>
          <button
            onClick={handleExit}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        
        {/* Intro Phase */}
        {gamePhase === 'intro' && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Get Ready</h2>
            <div className="text-7xl font-bold text-cyan-500">{introCountdown}</div>
            <p className="text-sm text-gray-500 mt-4">
              Memorize the clock readings of the critical instruments
            </p>
          </div>
        )}
        
        {/* Attention Phase - "Attention, Next item" */}
        {gamePhase === 'attention' && (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Attention</h2>
            <p className="text-xl text-gray-500 mt-3">Next item</p>
          </div>
        )}
        
        {/* Attribute Phase - Show critical parameter text only */}
        {gamePhase === 'attribute' && roundData && (
          <div className="text-center">
            <p className="text-lg text-gray-500 mb-4">Critical parameter:</p>
            <div className="inline-flex items-center px-10 py-6 rounded-xl bg-gray-100 border-2 border-gray-300">
              <span className="text-3xl font-bold text-gray-900 capitalize">
                {formatAttribute(roundData.criticalAttribute)}
              </span>
            </div>
          </div>
        )}
        
        {/* Display Phase - Show 3x3 grid of clocks */}
        {gamePhase === 'display' && roundData && (
          <FitToWidth>
            <div className="grid grid-cols-3 gap-0.5">
              {roundData.clocks.map((clock, idx) => (
                <ClockInstrument key={idx} clock={clock} size={175} />
              ))}
            </div>
          </FitToWidth>
        )}
        
        {/* Input Phase - Ask about each critical instrument */}
        {gamePhase === 'input' && roundData && (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              What value was indicated by the {ORDINALS[currentQuestionIdx]} 'critical' instrument?
            </h2>
            <Numpad 
              onSelect={handleAnswer}
              disabled={false}
            />
            <p className="text-xs text-gray-400 mt-4">
              Keyboard: 1-8 to select, 0 or 9 for "no idea"
            </p>
          </div>
        )}
        
        {/* Feedback Phase */}
        {gamePhase === 'feedback' && roundData && (
          <div className="text-center w-full max-w-2xl">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Round {currentRound + 1} Result
              </h2>
              <p className="text-sm text-gray-500">
                Critical attribute: <span className="font-medium capitalize text-cyan-500">{formatAttribute(roundData.criticalAttribute)}</span>
              </p>
            </div>
            
            {/* Show the grid with critical instruments highlighted */}
            <div className="grid grid-cols-3 gap-1 max-w-md mx-auto mb-4">
              {roundData.clocks.map((clock, idx) => (
                <div key={idx} className="relative">
                  <ClockInstrument 
                    clock={clock} 
                    size={120} 
                    highlight={clock.isCritical}
                  />
                  {clock.isCritical && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                      {roundData.criticalPositions.indexOf(idx) + 1}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Answer comparison */}
            <div className="grid grid-cols-4 gap-3 max-w-md mx-auto mb-4">
              {roundData.correctAnswers.map((correct, idx) => {
                const userAns = userAnswers[idx];
                const isCorrect = userAns === correct;
                return (
                  <div key={idx} className={`p-2 rounded-lg border-2 ${
                    isCorrect 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-red-500 bg-red-50'
                  }`}>
                    <p className="text-[10px] text-gray-500">#{idx + 1}</p>
                    <p className={`text-lg font-bold ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                      {userAns === 0 ? '?' : userAns}
                    </p>
                    {!isCorrect && (
                      <p className="text-xs text-gray-400">→ {correct}</p>
                    )}
                  </div>
                );
              })}
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              {userAnswers.filter((a, i) => a === roundData.correctAnswers[i]).length}/{CRITICAL_COUNT} correct
            </p>
            
            <button
              onClick={handleContinue}
              className="px-8 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-colors"
            >
              {currentRound + 1 >= TOTAL_ROUNDS ? 'View Results' : 'Next Round'}
            </button>
          </div>
        )}
        
        {/* Results Phase */}
        {gamePhase === 'results' && (
          <div className="text-center space-y-4 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900">Test Complete!</h2>
            
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              level === 1 ? 'bg-green-100 text-green-700' :
              level === 2 ? 'bg-yellow-100 text-yellow-700' :
              'bg-rose-100 text-rose-700'
            }`}>
              Level {level} {level === 3 ? '(Real Test)' : level === 2 ? '(Training)' : '(Practice)'}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-sm text-gray-600 mb-2">Final Score</p>
              <p className="text-5xl font-bold text-green-600">{finalPercentage}%</p>
              <p className="text-gray-500 mt-2">{totalCorrect} / {totalQuestions} correct</p>
            </div>
            
            {/* Per-round breakdown */}
            <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
              <p className="text-sm font-medium text-gray-700 mb-2">Round Details</p>
              <div className="grid grid-cols-5 gap-1 text-xs">
                {roundsData.map((r, idx) => (
                  <div key={idx} className={`p-1.5 rounded ${
                    r.correctCount === CRITICAL_COUNT ? 'bg-green-100 text-green-700' :
                    r.correctCount >= 2 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    <span className="font-medium">R{r.round}</span>
                    <span className="ml-1">{r.correctCount}/{CRITICAL_COUNT}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={() => navigate(`${gamesBase}/owt/${subjectId}`)}
              className="w-full py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
            >
              Back to Setup
            </button>
          </div>
        )}
      </div>
      
      {/* Debug Overlay */}
      {showDebug && (
        <div className="fixed top-14 left-2 bg-black/90 text-green-400 p-3 rounded-lg text-xs font-mono z-50 max-w-xs">
          <div className="text-yellow-400 mb-1">DEBUG (Shift+O to toggle)</div>
          <div>Phase: {gamePhase}</div>
          <div>Round: {currentRound + 1}/{TOTAL_ROUNDS}</div>
          <div>Level: {level} | Speed: {config.speed}</div>
          <div>Feedback: {feedbackEnabled ? 'ON' : 'OFF'}</div>
          {roundData && (
            <>
              <div className="mt-1 text-cyan-400">Current Round</div>
              <div>Attribute: {formatAttribute(roundData.criticalAttribute)}</div>
              <div>Critical positions: {roundData.criticalPositions.join(', ')}</div>
              <div>Correct answers: {roundData.correctAnswers.join(', ')}</div>
              <div>User answers: {userAnswers.join(', ') || '-'}</div>
              <div>Question: {currentQuestionIdx + 1}/4</div>
            </>
          )}
          <div className="mt-1">Score: {totalCorrect}/{totalQuestions} ({finalPercentage}%)</div>
        </div>
      )}
      
      {/* Exit Confirmation */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Exit Game?</h3>
            <p className="text-gray-600 mb-4">Your progress will not be saved.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmExit}
                className="flex-1 py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OWTGame;
